import Constants from "expo-constants";

/**
 * API base URL, resolved from app config (`extra.apiBaseUrl`) with an env override, so pointing the
 * app at local, staging or prod is a config change.
 */
const configuredApiBaseUrl: unknown = Constants.expoConfig?.extra?.apiBaseUrl;

export const API_BASE_URL: string = process.env.EXPO_PUBLIC_API_BASE_URL
  ?? (typeof configuredApiBaseUrl === "string" ? configuredApiBaseUrl : undefined)
  ?? "http://localhost:8000";

/** Public inventory web app paired with the configured API environment. */
export const APP_BASE_URL: string = process.env.EXPO_PUBLIC_APP_BASE_URL
  ?? (API_BASE_URL === "https://api-staging.trovie.io"
    ? "https://app-staging.trovie.io"
    : API_BASE_URL === "https://api.trovie.io"
    ? "https://app.trovie.io"
    : "http://localhost:8081");

/** Anonymous public Show host paired with the configured API environment. */
export const EVENTS_BASE_URL: string = process.env.EXPO_PUBLIC_EVENTS_BASE_URL
  ?? (API_BASE_URL === "https://api-staging.trovie.io"
    ? "https://events-staging.trovie.io"
    : API_BASE_URL === "https://api.trovie.io"
    ? "https://events.trovie.io"
    : typeof window !== "undefined" && typeof window.location?.origin === "string"
    ? window.location.origin
    : APP_BASE_URL);

/** Community home for feedback, support, and feature requests. */
export const DISCORD_URL = "https://discord.gg/jwNFswBYbz";

/** The help center (`help-center/`), served on the marketing domain. Overridable so a local run can
 *  read a locally built help center before it is published. */
export const HELP_CENTER_URL: string = process.env.EXPO_PUBLIC_HELP_CENTER_URL
  ?? "https://trovie.io/help";

/** Direct support contact for people who don't use Discord. */
export const SUPPORT_EMAIL = "support@trovie.io";

/** Published Privacy Policy, linked from sign-up consent and Settings (App Store Guideline 5.1.1(i)). */
export const PRIVACY_POLICY_URL = "https://trovie.io/privacy-policy";

/** Published Terms of Service, linked from sign-up consent alongside the Privacy Policy. */
export const TERMS_OF_SERVICE_URL = "https://trovie.io/terms-of-service";

/**
 * The public plans page the paywall's "Compare plans" link opens. Unset until that page ships
 * (TRO-1940), and the link is not rendered while it is empty, so no URL is guessed here.
 */
export const PRICING_URL: string = process.env.EXPO_PUBLIC_PRICING_URL ?? "";

/**
 * Clerk publishable key, which is non-secret and safe to embed client-side. It must match the
 * backend's TROVIE_CLERK_PUBLISHABLE_KEY (same Clerk instance), because tokens minted by another
 * instance fail the backend's JWKS verification.
 *
 * Empty rather than thrown when unset, so importing config does not crash every module that pulls
 * in the API client (most of the app, plus the test suite). `ClerkProvider` throws a clear
 * "Missing publishableKey" error when it mounts with an empty key.
 */
export const CLERK_PUBLISHABLE_KEY: string = process.env.EXPO_PUBLIC_CLERK_PUBLISHABLE_KEY ?? "";

/** Public Meta app ID required by Instagram's native Stories share target. */
export const META_APP_ID: string = process.env.EXPO_PUBLIC_META_APP_ID?.trim() ?? "";

/** Public PostHog project key for anonymous Show analytics; empty keeps analytics disabled. */
export const PUBLIC_POSTHOG_KEY: string = process.env.EXPO_PUBLIC_POSTHOG_KEY?.trim() ?? "";

/** PostHog ingest host, which must match the region the project key was issued in (TRO-490).
 *
 * Configuration rather than a constant because the key and the host must change together. A key
 * posted to another region's host is rejected, and because analytics never blocks the public
 * surface (ADR-0061), events stop arriving with no error on screen or in a log. Keeping the host
 * beside the key lets them be reviewed together.
 *
 * Defaults to the US cloud, the region ADR-0061 records, so a local build or a fork works without
 * it. Trovie's own deploys set it explicitly. */
export const PUBLIC_POSTHOG_HOST: string = process.env.EXPO_PUBLIC_POSTHOG_HOST?.trim()
  || "https://us.i.posthog.com";

/**
 * Public PostHog project key for identified product analytics in the authenticated app; empty keeps
 * it disabled, as `PUBLIC_POSTHOG_KEY` does for the anonymous surface.
 *
 * It currently names the same project as `PUBLIC_POSTHOG_KEY` but stays its own variable. ADR-0061
 * requires the identified path to use a bundle and event wrapper separate from the `BROWSE_ONLY`
 * bundle and anonymous wrapper, which `posthogAnalytics.ts` and `showAnalyticsClient.web.ts` are.
 * It does not require separate projects.
 *
 * A distinct variable means splitting projects later costs one env var, not a code change. The
 * split is needed once Vendor session replay is wanted, because that setting is project-wide and
 * ADR-0061 forbids it for attendees (TRO-631). A fallback to `PUBLIC_POSTHOG_KEY` would also arm
 * identified analytics wherever that key is set, including the `phc_test` value the e2e job uses.
 */
export const PUBLIC_POSTHOG_APP_KEY: string = process.env.EXPO_PUBLIC_POSTHOG_APP_KEY?.trim() ?? "";

/**
 * RevenueCat public SDK keys (`appl_...`, `goog_...`), one per store. They identify the app to
 * RevenueCat and are safe in the bundle; the secret API key and webhook token stay on the backend.
 * Empty disables store billing on that platform, so local dev, web and CI never reach the SDK.
 */
export const REVENUECAT_IOS_API_KEY: string = process.env.EXPO_PUBLIC_REVENUECAT_IOS_API_KEY?.trim()
  ?? "";
export const REVENUECAT_ANDROID_API_KEY: string =
  process.env.EXPO_PUBLIC_REVENUECAT_ANDROID_API_KEY?.trim() ?? "";

export function shouldUseLocalDevAuth(
  isDev: boolean,
  clerkKey: string,
  apiBaseUrl: string,
): boolean {
  if (!isDev || clerkKey) return false;
  try {
    const url = new URL(apiBaseUrl);
    return url.protocol === "http:"
      && (url.hostname === "localhost" || url.hostname === "127.0.0.1");
  } catch {
    return false;
  }
}

/** Local simulator convenience only; production/TestFlight bundles compile `__DEV__` to false. */
export const LOCAL_DEV_AUTH = shouldUseLocalDevAuth(
  __DEV__ && process.env.NODE_ENV !== "test",
  CLERK_PUBLISHABLE_KEY,
  API_BASE_URL,
);

/**
 * Marks a build whose only purpose is the anonymous browse and Show site (#387, TRO-424): the web
 * export published to the customer-facing Cloudflare Pages host (`browse.trovie.io`). Only the
 * browse deploy workflow sets it (`EXPO_PUBLIC_BROWSE_ONLY=1`), so normal iOS, Android and web
 * builds are untouched.
 *
 * When true, `app/(app)/_layout.tsx` renders the browse-host landing instead of the vendor app, so
 * no `(app)` route and no `ClerkProvider` mounts on that host. A customer who trims the QR link to
 * the host root gets a landing, not a vendor sign-in screen. `app/browse/*` and `app/shows/*` are
 * siblings of `(app)`, not descendants, so this switch does not affect them.
 */
export const BROWSE_ONLY: boolean = process.env.EXPO_PUBLIC_BROWSE_ONLY === "1";

/**
 * Marks a build whose only purpose is Trovie Admin, the internal cross-tenant dashboard (TRO-656,
 * ADR-0080): the web export published to `admin.trovie.io`. Only the admin deploy workflow sets it
 * (`EXPO_PUBLIC_ADMIN_ONLY=1`), so normal iOS, Android and web builds are untouched.
 *
 * It is not the mirror of `BROWSE_ONLY`. Browse-only short-circuits above `ClerkProvider`, because
 * that host serves anonymous customers and must never mount auth. Admin-only short-circuits below
 * it: admin users are authenticated staff, so the build keeps Clerk, the auth-token bridge and the
 * sign-in redirect as the vendor app has them, and swaps only the routes rendered inside. Reusing
 * that wiring avoids a second, subtly different copy of the token bridge.
 *
 * It is a convenience, not a security control. The server protects these figures:
 * `/api/v1/admin/*` returns 404 to anyone outside `TROVIE_ADMIN_EMAILS`, and the fact tier is read
 * through a Postgres role that cannot see any tenant-scoped table. This flag only decides which
 * screens a bundle ships.
 */
export const ADMIN_ONLY: boolean = process.env.EXPO_PUBLIC_ADMIN_ONLY === "1";
